import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ShieldCheck,
  Layers,
  User,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Tag,
  ShieldAlert,
} from 'lucide-react';
import {
  formatPaymentCurrency,
  formatPaymentDate,
  PaymentMethodIcon,
  RouteLogo,
  RouteOIcon,
  PageLoader,
} from '@repo/ui';

// ── Validation ────────────────────────────────────────────────────────────────
// BILL-123 format only — prevents path traversal / malformed requests
const BILL_NUMBER_RE = /^BILL-\d+$/i;

// ── VerifyBillPage ────────────────────────────────────────────────────────────

const VerifyBillPage = () => {
  const { t, i18n } = useTranslation();
  const { billNumber } = useParams<{ billNumber: string }>();
  const [searchParams] = useSearchParams();
  const lang = i18n.language;

  const sig = searchParams.get('sig') ?? '';
  const isValidFormat = BILL_NUMBER_RE.test(billNumber ?? '');

  const {
    data: bill,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['public-bill-verify', billNumber, sig],
    queryFn: () =>
      api
        .get(`/student/payments/bills/public/${billNumber}`, { params: { sig } })
        .then((res) => res.data?.data),
    enabled: !!billNumber && isValidFormat && !!sig,
    // Retry on network errors but not on 403/404
    retry: (failureCount, err: any) => {
      const status = err?.response?.status;
      if (status === 403 || status === 404) return false;
      return failureCount < 2;
    },
    staleTime: 1000 * 60 * 5, // 5 min — bill data doesn't change often
  });

  // ── Invalid URL format (no sig or bad bill number) ─────────────────────────
  if (!isValidFormat || !sig) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#f8fafc] p-6 text-center space-y-6">
        <div className="h-24 w-24 rounded-full bg-amber-50 flex items-center justify-center border border-amber-100 shadow-sm">
          <ShieldAlert className="h-12 w-12 text-amber-500" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-black text-slate-900">
            {t('student.receipt.verify.invalidTitle')}
          </h1>
          <p className="text-slate-500 max-w-xs mx-auto font-medium">
            {t('student.receipt.verify.invalidSubtitle')}
          </p>
        </div>
        <a
          href="https://routelms.com"
          className="px-10 py-3.5 rounded-2xl bg-[#002c8d] text-white font-black text-sm shadow-xl shadow-[#002c8d]/20 active:scale-95 transition-all"
        >
          {t('student.receipt.verify.returnHome')}
        </a>
      </div>
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <PageLoader
        message={t('student.receipt.verify.verifying')}
        fullscreen
        className="bg-[#f8fafc]"
      />
    );
  }

  // ── Error (403 = tampered sig, 404 = not found) ───────────────────────────
  if (isError || !bill) {
    const isForbidden = (error as any)?.response?.status === 403;
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#f8fafc] p-6 text-center space-y-6">
        <div className="h-24 w-24 rounded-full bg-rose-50 flex items-center justify-center border border-rose-100 shadow-sm">
          <XCircle className="h-12 w-12 text-rose-500" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-black text-slate-900">
            {t('student.receipt.verify.invalidTitle')}
          </h1>
          <p className="text-slate-500 max-w-xs mx-auto font-medium">
            {isForbidden
              ? t('student.receipt.verify.tamperedSubtitle', {
                  defaultValue: 'رابط التحقق غير صالح. يرجى مسح الـ QR Code الأصلي.',
                })
              : t('student.receipt.verify.invalidSubtitle')}
          </p>
        </div>
        <a
          href="https://routelms.com"
          className="px-10 py-3.5 rounded-2xl bg-[#002c8d] text-white font-black text-sm shadow-xl shadow-[#002c8d]/20 active:scale-95 transition-all"
        >
          {t('student.receipt.verify.returnHome')}
        </a>
      </div>
    );
  }

  const paymentDate = bill.createdAt ?? bill.date ?? bill.paidAt;

  return (
    <div className="min-h-screen bg-[#f8fafc] py-12 px-6 overflow-x-hidden">
      <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-6 duration-1000">
        {/* 1. Verification Status Banner */}
        <div className="flex items-center justify-center gap-3 py-4 bg-[#e9f7ef] border-2 border-[#d4efdf] rounded-[32px] text-[#27ae60] shadow-sm">
          <CheckCircle2 className="h-6 w-6" />
          <span className="text-lg font-black uppercase tracking-tight">
            {t('student.receipt.verify.verifiedBanner')}
          </span>
        </div>

        {/* 2. Main Premium Receipt Card */}
        <div className="relative rounded-[48px] border border-slate-200 bg-white shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] overflow-hidden">
          {/* Card Header (Blue Banner) */}
          <div className="bg-[#002c8d] p-10 text-white relative overflow-hidden">
            {/* Abstract glow */}
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

            <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
              {/* Brand Identity */}
              <div className="space-y-1 text-center md:text-start">
                <div className="h-12 flex items-center justify-center md:justify-start">
                  <RouteLogo className="h-full w-auto fill-white" />
                </div>
                <p className="text-white/80 text-[11px] font-black uppercase tracking-[0.2em] mt-3">
                  {t('student.receipt.verify.title')}
                </p>
              </div>

              {/* Route O Icon */}
              <div className="h-20 w-20 rounded-[2rem] bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 p-4 shadow-xl">
                <RouteOIcon className="h-full w-full text-white fill-current" />
              </div>
            </div>
          </div>

          <div className="p-10 md:p-14 space-y-12">
            {/* Amount Centered Section */}
            <div className="text-center space-y-3 py-10 bg-slate-50/50 rounded-[40px] border border-slate-100/50">
              <p className="text-sm font-black text-slate-400 uppercase tracking-[0.2em]">
                {t('student.receipt.verify.registeredAmount')}
              </p>
              <p className="text-7xl font-black text-[#27ae60] tabular-nums">
                {formatPaymentCurrency(bill.amount, lang)}
              </p>
            </div>

            {/* Metadata Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              {/* Receipt Ref */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {t('student.receipt.verify.receiptRef')}
                </p>
                <p className="text-xl font-black font-mono text-slate-900 tracking-wider">
                  {bill.number ?? billNumber}
                </p>
              </div>

              {/* Payment Method */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <PaymentMethodIcon method={bill.paymentMethod} className="h-3.5 w-3.5" />
                  {t('student.receipt.detail.paymentMethod')}
                </p>
                <div className="flex items-center gap-3 p-4 rounded-3xl bg-slate-50 border border-slate-100 w-fit min-w-[160px]">
                  <div className="h-9 w-9 rounded-2xl bg-white flex items-center justify-center shadow-sm text-slate-700">
                    <PaymentMethodIcon method={bill.paymentMethod} />
                  </div>
                  <span className="text-base font-black text-slate-900">
                    {t(`common.${bill.paymentMethod?.toLowerCase()}`, {
                      defaultValue: bill.paymentMethod,
                    })}
                  </span>
                </div>
              </div>

              {/* Student Identity */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <User className="h-3.5 w-3.5" />
                  {t('student.receipt.verify.student')}
                </p>
                <p className="text-xl font-black text-slate-900">{bill.student?.name ?? '---'}</p>
              </div>

              {/* Payment Date */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {t('student.receipt.verify.paymentDate')}
                </p>
                <p className="text-lg font-bold text-slate-900 leading-tight">
                  {formatPaymentDate(paymentDate, lang)}
                </p>
              </div>

              {/* Program Details */}
              {bill.program?.trackName && (
                <div className="space-y-3 md:col-span-2">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                    <Layers className="h-3.5 w-3.5" />
                    {t('student.receipt.verify.program')}
                  </p>
                  <p className="text-xl font-black text-slate-900 leading-tight">
                    {bill.program.trackName}
                  </p>
                </div>
              )}
            </div>

            {/* Perforated Divider */}
            <div className="pt-10 border-t border-dashed border-slate-100 text-center space-y-3">
              <p className="text-[13px] text-slate-400 font-bold leading-relaxed px-4">
                {t('student.receipt.verify.trustFooter')}
              </p>
              <div className="flex items-center justify-center gap-2 text-[11px] text-slate-300 font-black uppercase tracking-widest">
                <Tag className="h-3 w-3" />
                {t('student.receipt.verify.verifiedOn', {
                  date: new Date().toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US'),
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Branding Footer */}
        <div className="text-center opacity-20 hover:opacity-100 transition-opacity duration-500">
          <RouteLogo className="h-6 w-auto fill-slate-900 mx-auto" />
          <p className="text-[10px] font-black text-slate-900 uppercase tracking-[0.4em] mt-2">
            Route Academy &copy; 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyBillPage;
